import { useState } from 'react';
import { toPlainText } from '@portabletext/react';
import { navigationClickEvent, internalLinkClickEvent } from 'analytics/events/studio';
import {
  Typography,
  getTypographyDefaultColor,
  Button,
  Space,
  Container,
  Col,
  Row,
  SpanConfig,
  Modal
} from '@ag1/ui';
import clsx from 'clsx';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { BenefitsList as BenefitsListProps, SanityKeyed } from 'types/studio/studio';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import IngredientModal from 'shared/modals/IngredientModal/IngredientModal';
import { AccordionItem } from './AccordionItem/AccordionItem';
import CustomPortableText from '../PortableText/PortableText';
import styles from './BenefitsList.module.scss';
import { CommonSectionProps } from '../types';

const INFO_COL = { xxs: 12, m: 6, l: { value: 4, wide: true }, xl: 4 } satisfies SpanConfig;
const MEDIA_COL = { xxs: 12, m: 6 } satisfies SpanConfig;

export const BenefitsListBlockMarkup = ({
  _key: key,
  _type: sectionType,
  variant,
  headline,
  accordionItems,
  button,
  modalOptions,
  image,
  theme,
  padding,
  visibleOnPageLoad
}: SanityKeyed<BenefitsListProps> & CommonSectionProps) => {
  const reverse = variant === 'image_right';

  const [selected, setSelected] = useState<string | null>(null);

  const [isOpen, setOpen] = useState(false);

  const handleToggleAccordion = ({
    itemKey,
    itemHeadline
  }: {
    itemKey: string;
    itemHeadline: string;
  }) => {
    if (selected !== itemKey) {
      navigationClickEvent({
        header: headline,
        location: sectionType,
        navigationType: 'accordion',
        clickText: itemHeadline
      });
    }

    setSelected((prev) => (prev === itemKey ? null : itemKey));
  };

  const handleButtonClick = () => {
    const buttonText = modalOptions?.baseButton?.portableText
      ? toPlainText(modalOptions.baseButton.portableText)
      : undefined;
    internalLinkClickEvent({
      clickText: buttonText,
      location: sectionType
    });

    setOpen(true);
  };

  return (
    <>
      <Container narrow id={key}>
        <Space
          top={{ s: 60, m: 0 }}
          bottom={{ xxs: 40, s: 60, m: 0 }}
          className={clsx(getSectionPaddingClasses(styles, padding), styles.mandatoryNoPadding)}>
          <Row align="between" className={clsx(styles.row, reverse && styles.reverse)}>
            {image.image && (
              <Col span={MEDIA_COL} className={styles.mediaCol}>
                <div className={clsx(styles.mediaWrapper, reverse && styles.reverse)}>
                  <StudioBaseImageV2
                    image={image.image}
                    mobileImage={image.mobileImage}
                    withMobileImage={image.withMobileImage}
                    tabletImage={image.tabletImage}
                    className={styles.img}
                    sizes={MEDIA_COL}
                    eagerLoading={visibleOnPageLoad}
                  />
                </div>
              </Col>
            )}
            <Col span={INFO_COL} className={clsx(styles.infoCol, reverse && styles.reverse)}>
              <div className={clsx(styles.infoWrapper, reverse && styles.reverse)}>
                <Typography
                  variant="headlineSans"
                  size="2"
                  component="h2"
                  noPadding
                  color={getTypographyDefaultColor(theme)}>
                  {headline}
                </Typography>
                <div className={clsx(styles.questions)} data-testid="items-list">
                  {accordionItems.map(({ _key, iheadline, portableText }) => (
                    <AccordionItem
                      key={_key}
                      iheadline={iheadline}
                      portableText={portableText}
                      isOpen={_key === selected}
                      onToggle={() =>
                        handleToggleAccordion({ itemKey: _key, itemHeadline: iheadline })
                      }
                      theme={theme}
                    />
                  ))}
                </div>
                {button &&
                  modalOptions &&
                  modalOptions.baseButton &&
                  modalOptions.baseButton.portableText && (
                    <Space top={{ xxs: 40, xl: 48 }} className={styles.btnWrap}>
                      <Button
                        size={{ xxs: 'medium', l: 'large' }}
                        variant={modalOptions.baseButton.variant}
                        rounded
                        icon={
                          modalOptions.baseButton.showIcon &&
                          modalOptions.baseButton.iconOptions?.icon
                        }
                        iconPosition={modalOptions.baseButton.iconOptions?.position}
                        className={styles.btn}
                        data-gtm-skip-global
                        theme={theme}
                        onClick={handleButtonClick}>
                        <CustomPortableText value={modalOptions.baseButton.portableText} />
                      </Button>
                    </Space>
                  )}
              </div>
            </Col>
          </Row>
        </Space>
      </Container>
      <Modal isOpen={isOpen} onClose={() => setOpen(false)} mode="fullScreen">
        <div className={styles.modalInner}>
          {modalOptions?.modalImage?.image && (
            <IngredientModal isOpen={isOpen} onClose={() => setOpen(false)} />
          )}
        </div>
      </Modal>
    </>
  );
};

export default BenefitsListBlockMarkup;
